<template>
    <v-row
        v-if="showTimer"
        no-gutters
        class="header-timer"
        align="center"
    >
        <v-col>
            <v-tooltip
                bottom
                :nudge-top="-6"
                content-class="time-tooltip"
                open-delay="250"
            >
                <template v-slot:activator="{ on, attrs }">
                    <span
                        v-on="on"
                        class="tabular-nums"
                        :class="['title time-disp', { 'is-hovered': isHovered || status === 'running' }]"
                        @click="openTask(taskId)"
                    >
                        {{ (totalDuration + runningDuration + tickingDuration) | format_time_duration }}
                    </span>
                </template>
                <div class="d-flex align-start">
                    <div v-if="timerModule.task_initials && timerModule.task_initials.value" class="mt-1">
                        <AccountAvatar
                            :initials="timerModule.task_initials.value"
                            :color="timerModule.task_color.value"
                            tile
                            text-class="font-weight-bold"
                            :size="32"
                        />
                    </div>
                    <div class="ml-2">
                        <div class="body-2 main--text font-weight-medium">{{ timerModule.task_title.value }}</div>
                        <div class="d-flex align-center">
                            <div v-if="timerModule.task_client_name && timerModule.task_client_name.value" class="caption g4--text font-weight-medium mr-2">{{ timerModule.task_client_name.value }}</div>
                            <v-chip
                                v-if="timerModule.task_topic_name && timerModule.task_topic_name.value"
                                color="g1"
                                class="px-2"
                            >
                                <div class="caption g4--text font-weight-medium" style="line-height: 1rem">{{ timerModule.task_topic_name.value }}</div>
                            </v-chip>
                        </div>
                    </div>
                </div>
            </v-tooltip>
        </v-col>
        <v-col>
            <v-menu
                content-class="overflow-y-hidden dropmenu pa-1 elevation-0 set-background taskmenu-dropdown"
                :top="!$vuetify.breakpoint.mdAndUp"
                :bottom="$vuetify.breakpoint.mdAndUp"
                open-on-hover
                transition="slide-y-transition"
                offset-y
                nudge-left="16"
                :disabled="!canFinishTask"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        rounded
                        :class="['ml-2 round-btn', { 'is-running': status === 'running', 'is-paused': status !== 'running' }]"
                        v-bind="attrs"

                        v-on="on"
                        @mouseover="setIsHovered(true)"
                        @mouseleave="setIsHovered(false)"
                        @click="toggleTaskTimer"
                    >
                        <v-icon color="white">
                            {{
                                status === "running" ? "mdi-pause" : "mdi-play"
                            }}
                        </v-icon>
                    </v-btn>
                </template>
                <v-card
                    class="set-background"
                >
                    <v-row
                        no-gutters
                        justify="start"
                        align="start"
                    >
                        <v-col>
                            <v-col style="padding-top: 6px">
                                <button
                                    class="taskButton-item"
                                    @click="finishTask"
                                >
                                    <v-icon class="icon-block">
                                        mdi-check
                                    </v-icon>
                                    <span
                                        style="padding: 0 12px"
                                        class="body-2 truncate-text pr-6"
                                    >
                                        {{ $t("Mark as done") }}
                                    </span>
                                </button>
                            </v-col>
                        </v-col>
                    </v-row>
                </v-card>
            </v-menu>
        </v-col>
    </v-row>
</template>

<script>
import { computed } from "@vue/composition-api";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import TimerModule from "../../store/modules/timer";
import TaskModule from "../../store/modules/task";
import { ref } from "@vue/composition-api";
import AccountAvatar from "@/components/AccountAvatar";
import TimeEntryModule from "@/modules/timeEntry/timeEntry";
import AccountModule from "@/store/modules/account";

export default {
    name: "AppToolbarTracking",
    components: {
        AccountAvatar
    },
    setup() {
        const {available_company_accounts} = AccountModule();
        const timerModule = TimerModule();
        const { openTask } = TaskModule();
        const isHovered = ref(false);

        const showTimer = computed(function() {
            return (
                timerModule.task_id.value != null &&
                timerModule.task_id.value > 0 &&
                timerModule.task_status.value !== "finished" &&
                timerModule.can_be_started.value
            );
        });

        const toggleTaskTimer = function() {
            const company = available_company_accounts.value.find(x => x.id === timerModule.task_account_id.value)
            const billable = company && company.billing_settings ? company.billing_settings.time_billable : 1

            timerModule
                .executeAction(
                    timerModule.status.value === "running" ? "pause" : "start",
                    timerModule.task_id.value,
                    false,
                    null,
                    null,
                    billable
                )
                .catch(function(ex) {
                    errorDialog.showError(ex);
                });
        };

        const setIsHovered = val => {
            isHovered.value = val;
        };

        const canFinishTask = computed(() => timerModule.task_status.value !== 'finished')
        const finishTask = async () => {
            const company = available_company_accounts.value.find(x => x.id === timerModule.task_account_id.value)
            const billable = company && company.billing_settings ? company.billing_settings.time_billable : 1

            await TimeEntryModule.completeTask(timerModule.task_id.value, true, billable)
        }

        return {
            showTimer,
            openTask,
            taskId: timerModule.task_id,
            totalDuration: timerModule.total_duration,
            runningDuration: timerModule.running_duration,
            tickingDuration: timerModule.ticking_duration,
            status: timerModule.status,
            toggleTaskTimer,
            isHovered,
            setIsHovered,
            canFinishTask,
            finishTask,
            timerModule
        };
    }
};
</script>
<style scoped lang="scss">
    .icon-block {
        padding-top: 0 !important;
        background: var(--v-tretiary-base) !important;
        color: var(--v-white-base) !important;
    }
</style>
<style lang="scss">
    .time-tooltip.v-tooltip__content {
        padding: 12px 16px;
        background-color: var(--v-white-base) !important;
        box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.12), 0px 16px 32px rgba(0, 0, 0, 0.12), 0px 1px 48px rgba(0, 0, 0, 0.12);

        .avatar {
            span {
                font-size: 14px !important;
            }
        }

        .v-chip {
            height: unset;


            .v-chip__content {

            }
        }
    }
</style>